import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  bg, line, tx, font, accent, space, radius, attrColour,
} from '../theme';
import Button from '../components/Button';
import { useAuth } from '../lib/auth';
import { useProfile } from './ProfileContext';
import { saveMyProfile } from './accountApi';
import {
  DOF_ATTRS, DOF_BACKGROUNDS, bonusPointsFor, MAX_ATTR,
  baseAttrs, finalAttrs, overallRating, NATIONALITIES,
} from './dofData';
import { avatarsFor, GENDERS } from './avatars';
import { ageFromDob } from '../game/gameUtils';
import { PHILOSOPHIES, TRAITS, startingReputation, primaryTrait } from './reputationData';
import './ProfileEditor.css';

const TOTAL_STEPS = 5;
const STEPS = [
  { title: 'Who are you?', sub: 'The basics. Your name travels with you across every save.' },
  { title: 'Pick your look', sub: 'Set your gender to filter the portraits, then pick a face. Hover any to zoom in.' },
  { title: 'Your philosophy', sub: 'How you operate. It shapes your reputation, your board objective, and which clubs chase you.' },
  { title: 'Where you came from', sub: 'Your background shapes the attributes you start with.' },
  { title: 'Your strengths', sub: 'Spend your points to decide what you are best at.' },
];

const label = {
  fontFamily: font.display, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.14em', textTransform: 'uppercase', color: tx.muted,
  marginBottom: space.s2, display: 'block',
};
const input = {
  width: '100%', background: bg.base, color: tx.primary,
  border: `1px solid ${line.DEFAULT}`, borderRadius: radius.md,
  padding: '10px 12px', fontFamily: font.data, fontSize: 14, boxSizing: 'border-box',
};

function AvatarOption({ a, selected, onPick, initials }) {
  const [err, setErr] = useState(false);
  return (
    <button
      type="button"
      onClick={onPick}
      title="Hover to zoom"
      className="gw-head-opt"
      style={{
        width: 112, height: 112, borderRadius: '50%', padding: 0, cursor: 'pointer',
        border: `2px solid ${selected ? accent.lime : line.DEFAULT}`,
        background: bg.base, overflow: 'hidden', flexShrink: 0, position: 'relative',
        boxShadow: selected ? `0 0 0 3px ${accent.lime}44` : 'none',
      }}
    >
      {err ? (
        <span style={{
          width: '100%', height: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontFamily: font.display, fontWeight: 700,
          fontSize: 30, color: tx.faint, background: bg.raised,
        }}>{initials}</span>
      ) : (
        <img
          src={a.src}
          alt=""
          onError={() => setErr(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
    </button>
  );
}

// ── Live preview card ──────────────────────────────────────────────────────
function PreviewPortrait({ src, initials, size = 92 }) {
  const [err, setErr] = useState(false);
  const base = {
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    border: `2px solid ${accent.lime}`, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: bg.base,
  };
  if (!src || err) {
    return (
      <div style={{ ...base, border: `2px dashed ${line.strong}` }}>
        <span style={{ fontFamily: font.display, fontWeight: 700, fontSize: size * 0.34, color: tx.faint }}>
          {initials}
        </span>
      </div>
    );
  }
  return (
    <div style={base}>
      <img src={src} alt="" onError={() => setErr(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </div>
  );
}

function PreviewCard({
  firstName, lastName, avatarSrc, initials, age, nationality,
  background, final, overall, philosophy,
}) {
  const name = [firstName, lastName].filter(Boolean).join(' ');
  const metaBits = [age ? `Age ${age}` : null, nationality].filter(Boolean);
  const rep = philosophy ? startingReputation(philosophy) : null;
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: `linear-gradient(150deg, rgba(200,241,53,0.14), ${bg.surface} 62%)`,
      border: `1px solid ${accent.lime}`, borderRadius: radius.lg, padding: space.s5,
      boxShadow: '0 20px 50px -24px rgba(200,241,53,0.4)',
    }}>
      <div style={{
        fontFamily: font.display, fontSize: 10.5, fontWeight: 700,
        letterSpacing: '0.2em', textTransform: 'uppercase', color: accent.lime,
        marginBottom: space.s4,
      }}>Your Director</div>

      <div style={{ display: 'flex', gap: space.s4, alignItems: 'center', marginBottom: space.s4 }}>
        <PreviewPortrait src={avatarSrc} initials={initials} />
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: font.display, fontWeight: 700, fontSize: 20, lineHeight: 1.1,
            color: name ? tx.primary : tx.faint,
          }}>
            {name || 'Your name'}
          </div>
          <div style={{ fontFamily: font.data, fontSize: 12.5, color: tx.muted, marginTop: 4 }}>
            {metaBits.join(' · ')}
          </div>
          {background ? (
            <span style={{
              display: 'inline-block', marginTop: space.s2,
              fontFamily: font.display, fontSize: 11, fontWeight: 600,
              color: accent.lime, background: 'rgba(200,241,53,0.12)',
              border: `1px solid ${accent.lime}55`, borderRadius: 999, padding: '3px 10px',
            }}>{background}</span>
          ) : (
            <div style={{ fontFamily: font.data, fontSize: 11.5, color: tx.faint, marginTop: space.s2 }}>
              No background chosen yet
            </div>
          )}
        </div>
      </div>

      {final ? (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: space.s3,
            padding: `${space.s3}px 0`, marginBottom: space.s2,
            borderTop: `1px solid ${line.DEFAULT}`, borderBottom: `1px solid ${line.DEFAULT}`,
          }}>
            <span style={{
              fontFamily: font.display, fontSize: 10.5, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase', color: tx.muted,
            }}>Overall</span>
            <span style={{ fontFamily: font.data, fontSize: 26, fontWeight: 700, color: accent.lime, marginLeft: 'auto' }}>
              {overall}
            </span>
          </div>
          <div style={{ marginTop: space.s3 }}>
            {DOF_ATTRS.map((a) => {
              const val = final[a.key];
              return (
                <div key={a.key} style={{
                  display: 'grid', gridTemplateColumns: '1fr 60px 26px', alignItems: 'center',
                  gap: space.s2, padding: '3px 0',
                }}>
                  <span style={{ fontFamily: font.data, fontSize: 11.5, color: tx.secondary }}>{a.label}</span>
                  <div style={{ height: 6, background: bg.overlay, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${val}%`, height: '100%', background: accent.lime }} />
                  </div>
                  <span style={{ fontFamily: font.data, fontSize: 11.5, fontWeight: 700, color: attrColour(val), textAlign: 'right' }}>
                    {val}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div style={{
          borderTop: `1px solid ${line.DEFAULT}`, paddingTop: space.s4,
          fontFamily: font.data, fontSize: 12.5, color: tx.faint,
        }}>
          Your attributes appear once you pick a background.
        </div>
      )}

      {rep && (
        <div style={{ marginTop: space.s4, paddingTop: space.s4, borderTop: `1px solid ${line.DEFAULT}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: space.s3 }}>
            <span style={{
              fontFamily: font.display, fontSize: 10.5, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase', color: tx.muted,
            }}>Starting reputation</span>
            <span style={{ fontFamily: font.data, fontSize: 11.5, color: tx.muted }}>
              Standing <b style={{ color: tx.primary }}>{rep.standing}</b>
            </span>
          </div>
          {TRAITS.map((t) => (
            <div key={t.key} style={{
              display: 'grid', gridTemplateColumns: '1fr 58px 24px', alignItems: 'center',
              gap: space.s2, padding: '2.5px 0',
            }}>
              <span style={{ fontFamily: font.data, fontSize: 11.5, color: tx.secondary }}>{t.label}</span>
              <div style={{ height: 6, background: bg.overlay, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${rep[t.key]}%`, height: '100%', background: t.colour }} />
              </div>
              <span style={{ fontFamily: font.data, fontSize: 11.5, fontWeight: 700, color: t.colour, textAlign: 'right' }}>
                {rep[t.key]}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfileEditor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, reload } = useProfile();
  const editing = Boolean(profile);
  const d = profile?.data || {};

  const meta = user?.user_metadata || {};
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState(d.firstName || meta.firstName || '');
  const [lastName, setLastName] = useState(d.lastName || meta.lastName || '');
  const [dob, setDob] = useState(d.dob || '');
  const [nationality, setNationality] = useState(d.nationality || 'English');
  const [gender, setGender] = useState(d.gender || 'unspecified');
  const [avatar, setAvatar] = useState(d.avatar || '');
  const [background, setBackground] = useState(d.background || '');
  const [bonus, setBonus] = useState(d.bonus || {});
  const [philosophy, setPhilosophy] = useState(d.philosophy || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const base = background ? baseAttrs(background) : null;
  const final = background ? finalAttrs(background, bonus) : null;
  const pool = background ? bonusPointsFor(background) : 0;
  const spent = DOF_ATTRS.reduce((s, a) => s + (bonus[a.key] || 0), 0);
  const remaining = pool - spent;
  const overall = final ? overallRating(final) : null;
  const headshots = avatarsFor(gender);
  const initials = `${(firstName || '?')[0] || '?'}${(lastName || '')[0] || ''}`.toUpperCase();
  const age = ageFromDob(dob);
  const avatarObj = avatar ? headshots.find((a) => a.id === avatar) : null;
  const avatarSrc = avatarObj ? avatarObj.src : '';

  function chooseGender(value) {
    setGender(value);
    if (avatar && !avatarsFor(value).some((a) => a.id === avatar)) setAvatar('');
  }
  function chooseBackground(id) {
    setBackground(id);
    setBonus({});
  }
  function inc(key) {
    if (remaining <= 0) return;
    if ((final[key] || 0) >= MAX_ATTR) return;
    setBonus((b) => ({ ...b, [key]: (b[key] || 0) + 1 }));
  }
  function dec(key) {
    setBonus((b) => ({ ...b, [key]: Math.max(0, (b[key] || 0) - 1) }));
  }

  const canSave = firstName.trim() && lastName.trim() && background && philosophy && !busy;

  function canAdvance() {
    if (step === 1) return Boolean(firstName.trim() && lastName.trim());
    if (step === 3) return Boolean(philosophy);
    if (step === 4) return Boolean(background);
    return true;
  }
  function next() {
    if (step < TOTAL_STEPS && canAdvance()) { setStep(step + 1); scrollTop(); }
  }
  function back() {
    if (step > 1) { setStep(step - 1); scrollTop(); }
    else navigate('/hub');
  }
  function scrollTop() {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSave() {
    if (!canSave) return;
    setBusy(true); setError('');
    const data = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob, nationality, gender, avatar, background, bonus,
      attrs: finalAttrs(background, bonus),
      philosophy,
      reputation: startingReputation(philosophy),
    };
    const { error: err } = await saveMyProfile(user.id, data);
    setBusy(false);
    if (err) { setError(err.message || 'Could not save your profile.'); return; }
    await reload();
    navigate('/hub', { replace: true });
  }

  const meta5 = STEPS[step - 1];

  return (
    <div className="gw-pe">
      <div>
        {/* progress */}
        <div style={{ marginBottom: space.s5 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.s3,
          }}>
            <span style={{
              fontFamily: font.display, fontSize: 11, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase', color: accent.lime,
            }}>
              {editing ? 'Edit profile' : 'Create profile'} &middot; Step {step} of {TOTAL_STEPS}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <span key={i} style={{
                  width: i + 1 === step ? 22 : 8, height: 8, borderRadius: 999,
                  background: i + 1 <= step ? accent.lime : line.strong,
                  transition: 'width .2s ease, background .2s ease',
                }} />
              ))}
            </div>
          </div>
        </div>

        {/* animated step body */}
        <div key={step} className="gw-step-anim">
          <h1 style={{ fontFamily: font.display, fontSize: 26, fontWeight: 700, margin: 0 }}>
            {meta5.title}
          </h1>
          <p style={{ color: tx.secondary, margin: `${space.s2}px 0 ${space.s5}px`, maxWidth: '52ch' }}>
            {meta5.sub}
          </p>

          {step === 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.s4 }}>
              <div>
                <span style={label}>First name</span>
                <input style={input} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Alex" maxLength={40} />
              </div>
              <div>
                <span style={label}>Last name</span>
                <input style={input} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Marsh" maxLength={40} />
              </div>
              <div>
                <span style={label}>Date of birth</span>
                <input style={input} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
              </div>
              <div>
                <span style={label}>Nationality</span>
                <select style={input} value={nationality} onChange={(e) => setNationality(e.target.value)}>
                  {NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ marginBottom: space.s4 }}>
                <span style={label}>Gender</span>
                <div style={{ display: 'flex', gap: space.s2, maxWidth: 440 }}>
                  {GENDERS.map((g) => {
                    const sel = gender === g.value;
                    return (
                      <button
                        key={g.value}
                        type="button"
                        onClick={() => chooseGender(g.value)}
                        style={{
                          flex: 1, cursor: 'pointer', padding: '9px 10px', borderRadius: radius.md,
                          border: `1px solid ${sel ? accent.lime : line.DEFAULT}`,
                          background: sel ? bg.raised : bg.base,
                          color: sel ? accent.lime : tx.secondary,
                          fontFamily: font.display, fontSize: 13, fontWeight: 600,
                        }}
                      >
                        {g.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: space.s4,
                background: bg.surface, border: `1px solid ${line.DEFAULT}`,
                borderRadius: radius.lg, padding: space.s5,
              }}>
                {headshots.map((a) => (
                  <AvatarOption
                    key={a.id}
                    a={a}
                    initials={initials}
                    selected={avatar === a.id}
                    onPick={() => setAvatar(avatar === a.id ? '' : a.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: space.s3,
            }}>
              {DOF_BACKGROUNDS.map((b) => {
                const sel = background === b.id;
                const top = DOF_ATTRS
                  .map((a) => ({ label: a.label, w: b.weights[a.key] || 1 }))
                  .sort((x, y) => y.w - x.w).slice(0, 2).map((x) => x.label);
                const pts = bonusPointsFor(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => chooseBackground(b.id)}
                    style={{
                      textAlign: 'left', cursor: 'pointer',
                      background: sel ? bg.raised : bg.surface,
                      border: `1px solid ${sel ? accent.lime : line.DEFAULT}`,
                      borderRadius: radius.lg, padding: space.s4,
                    }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: space.s2, marginBottom: space.s2,
                    }}>
                      <span style={{
                        fontFamily: font.display, fontWeight: 700, fontSize: 15,
                        color: sel ? accent.lime : tx.primary,
                      }}>
                        {b.id}
                      </span>
                      <span title="Bonus attribute points this background gives you" style={{
                        marginLeft: 'auto', flexShrink: 0,
                        fontFamily: font.data, fontSize: 11, fontWeight: 700,
                        color: accent.gold, background: 'rgba(245,198,93,0.12)',
                        border: `1px solid ${accent.gold}55`, borderRadius: 999, padding: '2px 8px',
                      }}>+{pts} pts</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: tx.secondary, lineHeight: 1.5, marginBottom: space.s3 }}>
                      {b.blurb}
                    </div>
                    <div style={{ fontFamily: font.data, fontSize: 10.5, color: tx.muted, letterSpacing: '0.04em' }}>
                      STRONG IN: {top.join(', ')}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 5 && background && (
            <div style={{
              background: bg.surface, border: `1px solid ${line.DEFAULT}`,
              borderRadius: radius.lg, padding: space.s5,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', marginBottom: space.s3,
                fontFamily: font.display, fontSize: 12, fontWeight: 600,
              }}>
                <span style={{ color: tx.muted, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 11 }}>
                  Points to spend
                </span>
                <span style={{ marginLeft: 'auto', color: remaining > 0 ? accent.amber : accent.green, fontFamily: font.data, fontWeight: 700 }}>
                  {remaining} of {pool} left
                </span>
              </div>
              {DOF_ATTRS.map((a) => {
                const val = final[a.key];
                const b0 = base[a.key];
                const added = bonus[a.key] || 0;
                return (
                  <div key={a.key} style={{
                    display: 'grid', gridTemplateColumns: '150px 1fr auto', alignItems: 'center',
                    gap: space.s3, padding: `${space.s2}px 0`,
                  }}>
                    <span style={{ fontFamily: font.display, fontSize: 13, color: tx.secondary }}>{a.label}</span>
                    <div style={{ height: 8, background: bg.overlay, borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: 0, width: `${b0}%`, background: line.strong }} />
                      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${b0}%`, width: `${val - b0}%`, background: accent.lime }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: space.s2 }}>
                      <span style={{
                        fontFamily: font.data, fontSize: 14, fontWeight: 700,
                        color: attrColour(val), width: 28, textAlign: 'right',
                      }}>{val}</span>
                      <button type="button" onClick={() => dec(a.key)} disabled={added <= 0} style={stepBtn(added <= 0)}>-</button>
                      <button type="button" onClick={() => inc(a.key)} disabled={remaining <= 0 || val >= MAX_ATTR} style={stepBtn(remaining <= 0 || val >= MAX_ATTR)}>+</button>
                    </div>
                  </div>
                );
              })}
              <div style={{
                display: 'flex', alignItems: 'center', marginTop: space.s3, paddingTop: space.s3,
                borderTop: `1px solid ${line.DEFAULT}`,
              }}>
                <span style={{ fontFamily: font.display, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: tx.muted }}>
                  Overall
                </span>
                <span style={{ fontFamily: font.data, fontSize: 20, fontWeight: 700, color: accent.lime, marginLeft: space.s3 }}>
                  {overall}
                </span>
                <button
                  type="button"
                  onClick={() => setBonus({})}
                  style={{
                    marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
                    color: tx.faint, fontFamily: font.display, fontSize: 11, fontWeight: 600,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}
                >
                  Reset points
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(258px, 1fr))', gap: space.s3 }}>
              {PHILOSOPHIES.map((p) => {
                const sel = philosophy === p.key;
                const pt = primaryTrait(p.key);
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPhilosophy(p.key)}
                    style={{
                      textAlign: 'left', cursor: 'pointer',
                      background: sel ? bg.raised : bg.surface,
                      border: `1px solid ${sel ? accent.lime : line.DEFAULT}`,
                      borderRadius: radius.lg, padding: space.s4,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: space.s2, marginBottom: space.s2 }}>
                      <span style={{ fontFamily: font.display, fontWeight: 700, fontSize: 16, color: sel ? accent.lime : tx.primary }}>
                        {p.name}
                      </span>
                      {pt && (
                        <span style={{
                          marginLeft: 'auto', flexShrink: 0, fontFamily: font.data, fontSize: 10.5, fontWeight: 700,
                          color: pt.colour, background: `${pt.colour}1F`, border: `1px solid ${pt.colour}55`,
                          borderRadius: 999, padding: '2px 8px',
                        }}>{pt.label}</span>
                      )}
                    </div>
                    <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: 13, color: tx.secondary, marginBottom: space.s3 }}>
                      {p.tagline}
                    </div>
                    <div style={{ fontFamily: font.data, fontSize: 12, color: accent.green, lineHeight: 1.5, marginBottom: 4 }}>
                      {p.perk}
                    </div>
                    <div style={{ fontFamily: font.data, fontSize: 12, color: accent.amber, lineHeight: 1.5 }}>
                      {p.tradeoff}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <div style={{ color: accent.red, fontFamily: font.data, fontSize: 13, marginTop: space.s4 }}>{error}</div>
        )}

        {/* nav */}
        <div style={{
          display: 'flex', gap: space.s3, alignItems: 'center',
          marginTop: space.s6, paddingTop: space.s4, borderTop: `1px solid ${line.DEFAULT}`,
        }}>
          <Button variant="ghost" onClick={back}>
            {step === 1 ? 'Cancel' : '‹ Back'}
          </Button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: space.s3 }}>
            {step === 5 && remaining > 0 && (
              <span style={{ fontFamily: font.data, fontSize: 12, color: tx.faint }}>
                {remaining} points unspent
              </span>
            )}
            {step < TOTAL_STEPS ? (
              <Button onClick={next} disabled={!canAdvance()}>Next ›</Button>
            ) : (
              <Button onClick={handleSave} disabled={!canSave}>
                {busy ? 'Saving…' : (editing ? 'Save changes' : 'Create profile')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* live preview */}
      <div className="gw-pe-preview">
        <PreviewCard
          firstName={firstName.trim()}
          lastName={lastName.trim()}
          avatarSrc={avatarSrc}
          initials={initials}
          age={age}
          nationality={nationality}
          background={background}
          final={final}
          overall={overall}
          philosophy={philosophy}
        />
        <p style={{ fontFamily: font.data, fontSize: 11.5, color: tx.faint, margin: `${space.s3}px 0 0`, textAlign: 'center' }}>
          This is how your Director will appear.
        </p>
      </div>
    </div>
  );
}

function stepBtn(disabled) {
  return {
    width: 26, height: 26, borderRadius: radius.sm,
    border: `1px solid ${disabled ? line.DEFAULT : line.strong}`,
    background: disabled ? bg.surface : bg.raised,
    color: disabled ? tx.faint : tx.primary,
    fontFamily: font.data, fontSize: 16, lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };
}
